import { useTranslation } from 'react-i18next'
import {
  Select,
  TableColumns,
  TableColumnsColumnProps,
  Text,
} from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency, formatNumber } from '@/helpers'
import {
  OrderManagmentPurchaseOrderItemsResponseLinks,
  ProductQualityCategoryEnum,
} from '@/types'

import { SupplyOrderItemsWithLinksProps } from './supply-order-items-with-links.type'
import styles from './supply-order-items-with-links.module.css'

export const SupplyOrderItemsWithLinks = ({
  item,
  isLoading = false,
  onChangeCategory,
}: SupplyOrderItemsWithLinksProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplyOrderDetail')

  const AssociatedColumn = ({
    record,
  }: {
    record: OrderManagmentPurchaseOrderItemsResponseLinks
  }) => {
    const title =
      record.buyerCompanyName || t('supplyOrderItemsLinksAssociateEmptyColumn')

    return (
      <div>
        {title ? (
          <div>
            <Text>{title}</Text>
          </div>
        ) : null}
        {record?.reference ? (
          <div>
            <Text
              variant="text-small"
              className={styles['associate-column-reference']}
            >
              {record?.reference}
            </Text>
          </div>
        ) : null}
      </div>
    )
  }

  const columns: TableColumnsColumnProps<OrderManagmentPurchaseOrderItemsResponseLinks>[] =
    [
      {
        title: t('supplyOrderItemsLinksAssociateColumn'),
        key: 'associated-column',
        render: ({ record }) => <AssociatedColumn record={record} />,
      },
      {
        title: t('supplyOrderItemsLinksInflowColumn'),
        key: 'inflow-column',
        width: '10rem',
        render: ({ record }) => (
          <Text>
            {record?.quantity > 0
              ? formatNumber(record?.quantity, undefined, 0)
              : 0}{' '}
            KG
          </Text>
        ),
      },
      {
        title: t('supplyOrderItemsLinksOutflowColumn'),
        key: 'outflow-column',
        width: '10rem',
        render: ({ record }) => (
          <Text>
            {record?.quantity < 0
              ? formatNumber(record?.quantity, undefined, 0)
              : 0}{' '}
            KG
          </Text>
        ),
      },
      {
        title: t('supplyOrderItemsLinksStockColumn'),
        key: 'stock-column',
        width: '10rem',
        render: ({ record }) => (
          <Text>
            {formatNumber(record?.availableQuantity || 0, undefined, 0)} KG
          </Text>
        ),
      },
      {
        title: t('supplyOrderItemsLinksCostColumn'),
        key: 'cost-column',
        width: '10rem',
        render: ({ record }) => (
          <Text>
            {formatCurrency(
              record?.cost || 0,
              'es-MX',
              record?.currency || 'USD',
              DECIMAL_PLACES_DEFAULT,
            )}
          </Text>
        ),
      },
      {
        title: t('supplyOrderItemsLinksCategoryColumn'),
        key: 'category-column',
        width: '13rem',
        render: ({ record, index }) => {
          return index === 0 ? (
            <Select
              value={record?.category}
              onChange={(value) =>
                onChangeCategory?.(
                  item?.uuid || '',
                  value as keyof typeof ProductQualityCategoryEnum,
                )
              }
              options={[
                ...Object.keys(ProductQualityCategoryEnum).map((key) => ({
                  label: tEnums(`product_quality_category_${key}`),
                  value: key,
                })),
              ]}
            />
          ) : (
            ''
          )
        },
      },
      {
        title: t('supplyOrderItemsLinksTotalCostColumn'),
        key: 'total-cost-column',
        width: '16rem',
        render: ({ record }) => (
          <Text>
            {formatCurrency(
              record?.total,
              'es-MX',
              record?.currency || 'USD',
              DECIMAL_PLACES_DEFAULT,
            )}
          </Text>
        ),
      },
    ]

  return (
    <div>
      <Text variant="text-base" className={styles['product-header']}>
        {item?.name}
      </Text>
      <TableColumns
        columns={columns}
        data={item?.links || []}
        isLoading={isLoading}
        classNames={{ wrap: styles.table }}
      />
    </div>
  )
}
